import { ReactNode, useMemo } from "react"
import { currentDayFrameRows, required, useCurrentLog, useDataStore, useQuery, useRetainedEntities } from "../../Data"
import { beginCurrentDayLoad } from "../../Data/CurrentDayLoad"
import { SchemaEntities } from "../../Types/Generated/LogbookModel/Schema"
import { ILog, IStatus } from "../../Types"
import { FocusViewData, FocusViewState } from "./Context"
import { FocusDataContext, FocusDataViewData } from "./DataContext"
import { FocusFrameSink, useFocusFrame } from "./Frame"
import { createStoreFocusWriters } from "./writers"
import type { FocusViewProps } from "./Params"

// current.day chooses the active hierarchy; the shared store owns all its records.
export function FocusDataView({ initCruise, initTrip, initDay, initLogs, initStatuses, initCurrentLog, children }: FocusViewProps & { children?: ReactNode }) {
  const store = useDataStore();
  // Focus owns these reads even before the nested Day mounts. Retain before the
  // current-day request so its live replay can capture transitions during bootstrap.
  useRetainedEntities(["Log", "Status"]);

  const sink: FocusFrameSink = useMemo(() => ({
    beginLoad: () => beginCurrentDayLoad(store),
    seedFromInit: (seed) => {
      store.applyBundlePreservingLive({ entities: {
        ...currentDayFrameRows([seed.cruise], [seed.trip], [seed.day]),
        Log: seed.logs, Status: seed.statuses,
      } });
      if (seed.currentLog) {
        store.applyCurrentLog(seed.currentLog, "update");
      }
      store.replaceProvisionalLogs(seed.provisionalLogs);
    },
    clear: () => store.replaceProvisionalLogs([]),
  }), [store]);

  const { frame, editable } = useFocusFrame({ initCruise, initTrip, initDay, initLogs, initStatuses, initCurrentLog }, sink);
  const currentLog = useCurrentLog(store);

  // Today's day id scopes the store reads below. Derived from the frame + the current
  // log's timezone exactly as the UI derives `dayToday`; it is undefined until the frame
  // loads, so the queries mark it `required` and stay empty until then.
  const dayTodayId = useMemo(
    () => (frame ? FocusViewData.dayToday({ ...frame, logs: [], statuses: [] }, currentLog?.timezone)?.id : undefined),
    [frame, currentLog],
  );

  const logs = useQuery(store, SchemaEntities.Log, { dayId: required(dayTodayId) });
  const statuses = useQuery(store, SchemaEntities.Status, { dayId: required(dayTodayId) });

  const currentState: FocusViewState | undefined = useMemo(
    () => (frame ? { ...frame, logs: logs as ILog[], statuses: statuses as IStatus[] } : undefined),
    [frame, logs, statuses],
  );

  const writers = useMemo(
    () => createStoreFocusWriters(store, { dayId: dayTodayId }),
    [store, dayTodayId],
  );

  const value: FocusDataViewData = { currentState, currentLog, editable, writers };
  return <FocusDataContext.Provider value={value}>{children}</FocusDataContext.Provider>;
}
